<template>
  <Transition name="modal">
    <div
      v-if="showModal"
      @click="closeModal()"
      class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    >
      <div
        @click.stop
        class="relative w-full max-w-4xl bg-[var(--cream)] rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <!-- Close button -->
        <button
          @click="closeModal()"
          class="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white transition-all cursor-pointer"
          aria-label="Close modal"
        >
          <span class="material-symbols-outlined text-lg">close</span>
        </button>
        
        <!-- Modal content -->
        <div class="p-6">
          <div class="text-center mb-6">
            <h2 class="text-2xl font-bold text-[var(--forest)] italic mb-2">Safe Haven Wellness Group</h2>
            <div class="w-24 h-1 bg-[var(--terracotta)] rounded-full mx-auto"></div>
            <p class="text-sm text-[var(--moss)] italic mt-3">Intake Membership Application</p>
          </div>

          <!-- Progress Steps -->
          <div class="flex items-center mb-8 px-1">
            <template v-for="step in stepNumbers" :key="step">
              <div class="flex items-center" :class="step < totalSteps ? 'flex-1' : ''">
                <div
                  class="flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold transition-all flex-shrink-0"
                  :class="currentStep >= step ? 'bg-[var(--terracotta)] text-white' : 'bg-gray-300 text-gray-600'"
                >
                  {{ step }}
                </div>
                <div
                  v-if="step < totalSteps"
                  class="flex-1 h-1 mx-2 rounded-full transition-all"
                  :class="currentStep > step ? 'bg-[var(--terracotta)]' : 'bg-gray-300'"
                ></div>
              </div>
            </template>
          </div>

          <!-- Step 1: About You -->
          <div v-if="currentStep === 1" class="space-y-4">
            <h3 class="text-lg font-bold text-[var(--forest)] italic">1) About You</h3>
            <div>
              <label class="block text-xs font-semibold text-[var(--forest)] mb-1.5 italic">Full name:</label>
              <input v-model="formData.name" type="text" required
                     class="w-full bg-white border-2 border-[var(--sand)] rounded-full px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                     placeholder="Enter your full name" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-[var(--forest)] mb-1.5 italic">Mobile number:</label>
              <input v-model="formData.mobileNumber" type="tel"
                     class="w-full bg-white border-2 border-[var(--sand)] rounded-full px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                     placeholder="Enter your mobile number" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-[var(--forest)] mb-1.5 italic">Age (optional):</label>
              <input v-model="formData.age" type="number"
                     class="w-full bg-white border-2 border-[var(--sand)] rounded-full px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                     placeholder="Enter your age" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-[var(--forest)] mb-1.5 italic">Occupation (optional):</label>
              <input v-model="formData.occupation" type="text"
                     class="w-full bg-white border-2 border-[var(--sand)] rounded-full px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                     placeholder="Enter your occupation" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-[var(--forest)] mb-1.5 italic">Email (optional):</label>
              <input v-model="formData.email" type="email"
                     class="w-full bg-white border-2 border-[var(--sand)] rounded-full px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                     placeholder="Enter your email address" />
            </div>
          </div>

          <!-- Step 1: Understanding Safe Haven & Experiences -->
          <div v-if="currentStep === 1" class="space-y-6 mt-6">
            <div class="space-y-4">
              <h3 class="text-lg font-bold text-[var(--forest)] italic">Understanding Safe Haven</h3>
              <p class="text-sm text-[var(--moss)] italic mb-4">Please review and acknowledge:</p>
              <div class="space-y-3">
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.understanding.notClinical" class="mt-1" required>
                  <span class="text-sm text-[var(--forest)]">I understand Safe Haven is a lived-experience–led wellness group, not a clinical therapy or psychiatric service.</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.understanding.notEmergency" class="mt-1" required>
                  <span class="text-sm text-[var(--forest)]">I understand this is not an emergency or crisis-response service.</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.understanding.placementDetermined" class="mt-1" required>
                  <span class="text-sm text-[var(--forest)]">I understand placement within Safe Haven is determined by facilitation team based on readiness and responses.</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.understanding.agreeGuidelines" class="mt-1" required>
                  <span class="text-sm text-[var(--forest)]">I agree to follow safety, confidentiality, and participation guidelines if accepted.</span>
                </label>
              </div>
            </div>
            
            <div class="space-y-4 mt-6">
              <h3 class="text-lg font-bold text-[var(--forest)] italic">Experiences That Have Shaped You</h3>
              <p class="text-sm text-[var(--moss)] italic mb-4">Please select any themes that reflect your lived experience. No details are required.</p>
              <div class="space-y-2">
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.experiences.suicidalThoughts" class="mt-1">
                  <span class="text-sm text-[var(--forest)]">Experiences with suicidal thoughts (past or ongoing)</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.experiences.selfHarm" class="mt-1">
                  <span class="text-sm text-[var(--forest)]">Experiences with self-harm (past)</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.experiences.trauma" class="mt-1">
                  <span class="text-sm text-[var(--forest)]">Trauma or overwhelming life experiences</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.experiences.repeatingPatterns" class="mt-1">
                  <span class="text-sm text-[var(--forest)]">Repeating emotional or behavioural patterns you want to change</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.experiences.lowMood" class="mt-1">
                  <span class="text-sm text-[var(--forest)]">Persistent low mood or loss of motivation</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.experiences.anxiety" class="mt-1">
                  <span class="text-sm text-[var(--forest)]">Anxiety, panic, or chronic worry</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.experiences.identityDistress" class="mt-1">
                  <span class="text-sm text-[var(--forest)]">Identity-related distress (sense of self, belonging, worth)</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.experiences.grief" class="mt-1">
                  <span class="text-sm text-[var(--forest)]">Grief or major life transitions</span>
                </label>
                <div class="flex items-start gap-3">
                  <input type="checkbox" v-model="formData.experiences.other" class="mt-1">
                  <input v-model="formData.experiences.otherDescription" type="text" 
                         class="flex-1 bg-white border-2 border-[var(--sand)] rounded-full px-4 py-2 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                         placeholder="Other (describe briefly)" />
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Current Situation -->
          <div v-if="currentStep === 2" class="space-y-6">
            <div class="space-y-4">
              <h3 class="text-lg font-bold text-[var(--forest)] italic">2) My Situation Lately</h3>
              <p class="text-sm text-[var(--moss)] italic mb-4">Please answer honestly. These questions help us assess readiness and safety.</p>
              <div class="space-y-4">
                <div>
                  <p class="text-sm text-[var(--forest)] mb-2">Over the past 2 weeks, I have generally been able to keep myself safe:</p>
                  <div class="flex gap-4">
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.keepSafe" value="Yes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Yes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.keepSafe" value="Sometimes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Sometimes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.keepSafe" value="No" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">No</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <p class="text-sm text-[var(--forest)] mb-2">I keep experiencing flashbacks, nightmares, and intrusive thoughts about something that happened:</p>
                  <div class="flex gap-4">
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.flashbacks" value="Yes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Yes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.flashbacks" value="Sometimes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Sometimes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.flashbacks" value="No" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">No</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <p class="text-sm text-[var(--forest)] mb-2">I am currently seeking professional psychiatric help, and I am currently on medication as prescribed by my psychiatrist:</p>
                  <div class="flex gap-4">
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.psychiatricHelp" value="Yes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Yes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.psychiatricHelp" value="Sometimes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Sometimes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.psychiatricHelp" value="No" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">No</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <p class="text-sm text-[var(--forest)] mb-2">I currently have at least one form of external support (friend, family member, counsellor, community support, or hotline):</p>
                  <div class="flex gap-4">
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.externalSupport" value="Yes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Yes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.externalSupport" value="No" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">No</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <p class="text-sm text-[var(--forest)] mb-2">When I feel emotionally activated, I am usually able to pause, ground myself, or seek support:</p>
                  <div class="flex gap-4">
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.emotionalRegulation" value="Yes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Yes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.emotionalRegulation" value="Sometimes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Sometimes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.emotionalRegulation" value="No" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">No</span>
                    </label>
                  </div>
                </div>
                
                <div>
                  <p class="text-sm text-[var(--forest)] mb-2">I can engage in shared spaces without sharing graphic or highly triggering details:</p>
                  <div class="flex gap-4">
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.noTriggeringDetails" value="Yes" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Yes</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="radio" v-model="formData.situation.noTriggeringDetails" value="Unsure" class="text-[var(--terracotta)]">
                      <span class="text-sm text-[var(--forest)]">Unsure</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Support Focus -->
          <div v-if="currentStep === 3" class="space-y-4">
            <h3 class="text-lg font-bold text-[var(--forest)] italic">3) Support Focus</h3>
            <div>
              <p class="text-sm text-[var(--forest)] mb-1">What specific area of your life is currently causing you the most stress or difficulty, and what has led you to seek support at this particular moment?</p>
              <p class="text-xs text-[var(--moss)] italic mb-2">We invite you to share this in your own words (not more than 100 words). You might focus on challenges related to your daily routine, relationships, academic or professional life, or your overall sense of well-being. Understanding what you are experiencing right now helps us connect you with the most appropriate resources and peers.</p>
              <textarea v-model="formData.supportFocus.stressArea" rows="4" required
                        class="w-full bg-white border-2 border-[var(--sand)] rounded-lg px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                        placeholder="Share what's on your mind..."></textarea>
              <p class="text-xs mt-1 text-right" :class="stressAreaWordCount > 100 ? 'text-red-600 font-semibold' : 'text-[var(--moss)]'">{{ stressAreaWordCount }}/100 words</p>
            </div>

            <div class="space-y-3 mt-6">
              <h3 class="text-lg font-bold text-[var(--forest)] italic">Support Track Alignment</h3>
              <p class="text-sm text-[var(--moss)] italic mb-2">Our cohort runs three distinct, focused support tracks to ensure everyone is surrounded by peers facing similar life challenges. Based on what you are experiencing right now, which of these areas do you feel best aligns with the support you need? Please read the brief snapshots below and select the single track that resonates most with your current situation.</p>

              <label class="track-option flex items-start gap-3 cursor-pointer p-3 bg-white/70 border border-[var(--sand)] rounded-lg">
                <input type="radio" v-model="formData.supportFocus.track" value="suicide-self-harm" class="mt-1" required>
                <span class="text-sm text-[var(--forest)]">
                  <strong class="block mb-1">Track 1: Suicide and Self-Harm Support</strong>
                  This track is for individuals experiencing intense, overwhelming pain who may be struggling with active thoughts of ending their life, impulses to harm themselves, or a feeling that they can no longer keep themselves safe.
                </span>
              </label>

              <label class="track-option flex items-start gap-3 cursor-pointer p-3 bg-white/70 border border-[var(--sand)] rounded-lg">
                <input type="radio" v-model="formData.supportFocus.track" value="identity-depression-anxiety" class="mt-1" required>
                <span class="text-sm text-[var(--forest)]">
                  <strong class="block mb-1">Track 2: Identity, Depression and Anxiety Support</strong>
                  This track is for individuals navigating deep waves of persistent sadness/low motivation, constant worrying or panic, or feeling disconnected from who they are, their identity, and their purpose.
                </span>
              </label>

              <label class="track-option flex items-start gap-3 cursor-pointer p-3 bg-white/70 border border-[var(--sand)] rounded-lg">
                <input type="radio" v-model="formData.supportFocus.track" value="trauma-behavioural" class="mt-1" required>
                <span class="text-sm text-[var(--forest)]">
                  <strong class="block mb-1">Track 3: Trauma and Behavioural Patterns</strong>
                  This track is for individuals working through the lasting impact of difficult or overwhelming ongoing/past events, which may show up today as intrusive memories/flashbacks, constant emotional alertness (hypervigilance), or feeling emotionally numb.
                </span>
              </label>
            </div>
          </div>

          <!-- Step 3: Goals for Joining Safe Haven -->
          <div v-if="currentStep === 3" class="space-y-2">
            <h3 class="text-lg font-bold text-[var(--forest)] italic">Goals for Joining Safe Haven</h3>
            <p class="text-xs text-[var(--moss)] italic mb-2">In 2-4 sentences, please describe what you are hoping to gain from participating in Safe Haven at this stage of your journey.</p>
            <textarea v-model="formData.goals" rows="4" required
                      class="w-full bg-white border-2 border-[var(--sand)] rounded-lg px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                      placeholder="Share your goals for joining Safe Haven..."></textarea>
          </div>

          <!-- Step 3: Readiness for Group-Based Wellness Spaces -->
          <div v-if="currentStep === 3" class="space-y-2">
            <h3 class="text-lg font-bold text-[var(--forest)] italic">Readiness for Group-Based Wellness Spaces</h3>
            <p class="text-xs text-[var(--moss)] italic mb-2">Please reflect on your current capacity for group participation.</p>
            <div class="space-y-2">
              <div class="p-3 bg-white/70 border border-[var(--sand)] rounded-lg">
                <p class="text-sm text-[var(--forest)] mb-1">I can respect my own boundaries and the boundaries of others in group spaces:</p>
                <div class="flex gap-3 flex-wrap">
                  <label class="flex items-center gap-2">
                    <input type="radio" v-model="formData.readiness.respectBoundaries" value="Yes" class="text-[var(--terracotta)]">
                    <span class="text-sm text-[var(--forest)]">Yes</span>
                  </label>
                  <label class="flex items-center gap-2">
                    <input type="radio" v-model="formData.readiness.respectBoundaries" value="Unsure" class="text-[var(--terracotta)]">
                    <span class="text-sm text-[var(--forest)]">Unsure</span>
                  </label>
                </div>
              </div>
              
              <div class="p-3 bg-white/70 border border-[var(--sand)] rounded-lg">
                <p class="text-sm text-[var(--forest)] mb-1">I am open to learning and practicing emotional regulation or grounding skills:</p>
                <div class="flex gap-3 flex-wrap">
                  <label class="flex items-center gap-2">
                    <input type="radio" v-model="formData.readiness.openToSkills" value="Yes" class="text-[var(--terracotta)]">
                    <span class="text-sm text-[var(--forest)]">Yes</span>
                  </label>
                  <label class="flex items-center gap-2">
                    <input type="radio" v-model="formData.readiness.openToSkills" value="Somewhat" class="text-[var(--terracotta)]">
                    <span class="text-sm text-[var(--forest)]">Somewhat</span>
                  </label>
                  <label class="flex items-center gap-2">
                    <input type="radio" v-model="formData.readiness.openToSkills" value="No" class="text-[var(--terracotta)]">
                    <span class="text-sm text-[var(--forest)]">No</span>
                  </label>
                </div>
              </div>
              
              <div class="p-3 bg-white/70 border border-[var(--sand)] rounded-lg">
                <p class="text-sm text-[var(--forest)] mb-1">I am able to receive feedback or guidance from facilitators when needed:</p>
                <div class="flex gap-3 flex-wrap">
                  <label class="flex items-center gap-2">
                    <input type="radio" v-model="formData.readiness.receiveFeedback" value="Yes" class="text-[var(--terracotta)]">
                    <span class="text-sm text-[var(--forest)]">Yes</span>
                  </label>
                  <label class="flex items-center gap-2">
                    <input type="radio" v-model="formData.readiness.receiveFeedback" value="Sometimes" class="text-[var(--terracotta)]">
                    <span class="text-sm text-[var(--forest)]">Sometimes</span>
                  </label>
                  <label class="flex items-center gap-2">
                    <input type="radio" v-model="formData.readiness.receiveFeedback" value="No" class="text-[var(--terracotta)]">
                    <span class="text-sm text-[var(--forest)]">No</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Support Needs Check -->
          <div v-if="currentStep === 3" class="space-y-2">
            <h3 class="text-lg font-bold text-[var(--forest)] italic">Support Needs Check</h3>
            <p class="text-xs text-[var(--moss)] italic mb-2">Please indicate if any of the following are currently true for you:</p>
            <div class="space-y-2">
              <label class="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" v-model="formData.supportNeeds.constantSupervision" class="mt-1">
                <span class="text-sm text-[var(--forest)]">I am unable to keep myself safe without constant supervision from friends/family.</span>
              </label>
              <label class="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" v-model="formData.supportNeeds.lossOfReality" class="mt-1">
                <span class="text-sm text-[var(--forest)]">I am experiencing frequent loss of contact with reality (e.g., severe confusion or hallucinations).</span>
              </label>
              <label class="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" v-model="formData.supportNeeds.dailyDifficulties" class="mt-1">
                <span class="text-sm text-[var(--forest)]">I have noticed changes in my mood, sleep, and/or daily concentration that make it difficult to carry out my day-to-day activities.</span>
              </label>
            </div>
          </div>

          <!-- Step 4: Consent & Acknowledgement -->
          <div v-if="currentStep === 4" class="space-y-4">
            <h3 class="text-lg font-bold text-[var(--forest)] italic">4) Consent & Acknowledgement</h3>
            <div class="space-y-4">
              <div class="space-y-3">
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.consent.reviewed" class="mt-1" required>
                  <span class="text-sm text-[var(--forest)]">I consent to my responses being reviewed by the Safe Haven facilitation team for the purpose of readiness and placement decisions.</span>
                </label>
                <label class="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" v-model="formData.consent.voluntary" class="mt-1" required>
                  <span class="text-sm text-[var(--forest)]">I understand participation is voluntary, and I may withdraw my application at any time.</span>
                </label>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div>
                  <label class="block text-xs font-semibold text-[var(--forest)] mb-1.5 italic">Name:</label>
                  <input v-model="formData.name" type="text" required
                         class="w-full bg-white border-2 border-[var(--sand)] rounded-full px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all"
                         placeholder="Enter your full name" />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-[var(--forest)] mb-1.5 italic">Date:</label>
                  <input v-model="formData.date" type="date" required
                         class="w-full bg-white border-2 border-[var(--sand)] rounded-full px-4 py-2.5 text-sm text-[var(--forest)] placeholder:text-[var(--moss)]/60 focus:ring-2 focus:ring-[var(--terracotta)] focus:border-[var(--terracotta)] italic shadow-sm transition-all" />
                </div>
              </div>

              <!-- Success/Error Message -->
              <div v-if="submitStatus" class="p-4 rounded-lg text-sm" :class="submitStatus === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-base">
                    {{ submitStatus === 'success' ? 'check_circle' : 'error' }}
                  </span>
                  <span>{{ submitMessage }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation Buttons -->
          <div class="flex justify-between mt-8">
            <button v-if="currentStep > 1" @click="previousStep" 
                    class="px-6 py-2.5 border-2 border-[var(--forest)] text-[var(--forest)] rounded-full text-sm font-bold hover:bg-[var(--forest)]/10 transition-all">
              Previous
            </button>
            <div v-else></div>
            
            <button v-if="currentStep < totalSteps" @click="nextStep" 
                    :disabled="!isCurrentStepValid"
                    class="px-6 py-2.5 bg-[var(--forest)] text-white rounded-full text-sm font-bold hover:bg-[var(--forest)]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              Next
            </button>
            
            <button v-if="currentStep === totalSteps" @click="handleSubmit" 
                    :disabled="isSubmitting || submitStatus === 'success' || !isCurrentStepValid"
                    class="px-6 py-2.5 bg-[var(--terracotta)] text-white rounded-full text-sm font-bold hover:bg-[var(--terracotta)]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              <span v-if="isSubmitting" class="material-symbols-outlined animate-spin">progress_activity</span>
              <span v-else-if="submitStatus === 'success'" class="material-symbols-outlined">check_circle</span>
              <span v-else>Submit Application</span>
            </button>
          </div>
        </div>
      </div>
      </div>
    </Transition>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export default {
  name: 'JoinCommunityModal',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const getTodayDate = () => new Date().toISOString().split('T')[0]
    const showModal = ref(false)
    const currentStep = ref(1)
    const totalSteps = 4
    const stepNumbers = Array.from({ length: totalSteps }, (_, index) => index + 1)
    const isSubmitting = ref(false)
    const submitStatus = ref('') // 'success' or 'error'
    const submitMessage = ref('')
    
    const formData = ref({
      // Step 1: About You
      mobileNumber: '',
      age: '',
      occupation: '',
      email: '',
      
      // Step 2: Understanding Safe Haven
      understanding: {
        notClinical: false,
        notEmergency: false,
        placementDetermined: false,
        agreeGuidelines: false
      },
      
      // Step 3: Experiences
      experiences: {
        suicidalThoughts: false,
        selfHarm: false,
        trauma: false,
        repeatingPatterns: false,
        lowMood: false,
        anxiety: false,
        identityDistress: false,
        grief: false,
        other: false,
        otherDescription: ''
      },
      
      // Step 4: My Situation Lately
      situation: {
        keepSafe: '',
        flashbacks: '',
        psychiatricHelp: '',
        externalSupport: '',
        emotionalRegulation: '',
        noTriggeringDetails: ''
      },
      
      // Step 5: Support Focus
      supportFocus: {
        stressArea: '',
        track: ''
      },
      
      // Step 6: Goals for Joining Safe Haven
      goals: '',
      
      // Step 7: Readiness for Group-Based Wellness Spaces
      readiness: {
        respectBoundaries: '',
        openToSkills: '',
        receiveFeedback: ''
      },
      
      // Step 8: Support Needs Check
      supportNeeds: {
        constantSupervision: false,
        lossOfReality: false,
        dailyDifficulties: false
      },
      
      // Step 9: Consent & Acknowledgement
      consent: {
        reviewed: false,
        voluntary: false
      },
      
      // Additional info
      name: '',
      date: getTodayDate()
    })

    const openModal = () => {
      showModal.value = true
      document.body.style.overflow = 'hidden'
    }

    const isJoinRoute = () => route.name === 'Join' || route.hash === '#join'

    const closeModal = ({ clearRoute = true } = {}) => {
      showModal.value = false
      document.body.style.overflow = ''
      currentStep.value = 1
      resetFormData()
      isSubmitting.value = false
      submitStatus.value = ''
      submitMessage.value = ''
      if (!clearRoute) return
      if (route.name === 'Join') {
        router.replace({ name: 'Home' })
      } else if (route.hash === '#join') {
        router.replace({ hash: '' })
      }
    }

    const resetFormData = () => {
      formData.value = {
        name: '',
        mobileNumber: '',
        age: '',
        occupation: '',
        email: '',
        understanding: {
          notClinical: false,
          notEmergency: false,
          placementDetermined: false,
          agreeGuidelines: false
        },
        experiences: {
          suicidalThoughts: false,
          selfHarm: false,
          trauma: false,
          repeatingPatterns: false,
          lowMood: false,
          anxiety: false,
          identityDistress: false,
          grief: false,
          other: false,
          otherDescription: ''
        },
        situation: {
          keepSafe: '',
          flashbacks: '',
          psychiatricHelp: '',
          externalSupport: '',
          emotionalRegulation: '',
          noTriggeringDetails: ''
        },
        supportFocus: {
          stressArea: '',
          track: ''
        },
        goals: '',
        readiness: {
          respectBoundaries: '',
          openToSkills: '',
          receiveFeedback: ''
        },
        supportNeeds: {
          constantSupervision: false,
          lossOfReality: false,
          dailyDifficulties: false
        },
        consent: {
          reviewed: false,
          voluntary: false
        },
        date: getTodayDate()
      }
    }

    const stressAreaWordCount = computed(() => {
      const text = formData.value.supportFocus.stressArea.trim()
      return text ? text.split(/\s+/).length : 0
    })

    const isCurrentStepValid = computed(() => {
      switch (currentStep.value) {
        case 1:
          return formData.value.name &&
                 formData.value.understanding.notClinical && 
                 formData.value.understanding.notEmergency && 
                 formData.value.understanding.placementDetermined && 
                 formData.value.understanding.agreeGuidelines
        case 2:
          return formData.value.situation.keepSafe &&
                 formData.value.situation.flashbacks &&
                 formData.value.situation.psychiatricHelp &&
                 formData.value.situation.externalSupport &&
                 formData.value.situation.emotionalRegulation &&
                 formData.value.situation.noTriggeringDetails
        case 3:
          return formData.value.supportFocus.stressArea.trim() &&
                 stressAreaWordCount.value <= 100 &&
                 formData.value.supportFocus.track &&
                 formData.value.goals.trim() &&
                 formData.value.readiness.respectBoundaries && 
                 formData.value.readiness.openToSkills && 
                 formData.value.readiness.receiveFeedback
        case 4:
          return formData.value.consent.reviewed && 
                 formData.value.consent.voluntary &&
                 formData.value.name && 
                 formData.value.date
        default:
          return false
      }
    })

    const nextStep = () => {
      if (currentStep.value < totalSteps) {
        currentStep.value++
        // Smooth scroll to top of form after step change
        setTimeout(() => {
          const formElement = document.querySelector('.max-h-\\[60vh\\]')
          if (formElement) {
            formElement.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }, 100)
      }
    }

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }

    const handleSubmit = async () => {
      isSubmitting.value = true
      submitStatus.value = ''
      submitMessage.value = ''
      
      try {
        // Format experiences for email
        const selectedExperiences = []
        if (formData.value.experiences.suicidalThoughts) selectedExperiences.push('Experiences with suicidal thoughts')
        if (formData.value.experiences.selfHarm) selectedExperiences.push('Experiences with self-harm')
        if (formData.value.experiences.trauma) selectedExperiences.push('Trauma or overwhelming life experiences')
        if (formData.value.experiences.repeatingPatterns) selectedExperiences.push('Repeating emotional or behavioural patterns')
        if (formData.value.experiences.lowMood) selectedExperiences.push('Persistent low mood or loss of motivation')
        if (formData.value.experiences.anxiety) selectedExperiences.push('Anxiety, panic, or chronic worry')
        if (formData.value.experiences.identityDistress) selectedExperiences.push('Identity-related distress')
        if (formData.value.experiences.grief) selectedExperiences.push('Grief or major life transitions')
        if (formData.value.experiences.other && formData.value.experiences.otherDescription) {
          selectedExperiences.push(`Other: ${formData.value.experiences.otherDescription}`)
        }
        const selectedSupportNeeds = []
        if (formData.value.supportNeeds.constantSupervision) selectedSupportNeeds.push('Unable to keep safe without constant supervision')
        if (formData.value.supportNeeds.lossOfReality) selectedSupportNeeds.push('Frequent loss of contact with reality')
        if (formData.value.supportNeeds.dailyDifficulties) selectedSupportNeeds.push('Mood/sleep/concentration challenges affecting day-to-day activities')

        const trackLabels = {
          'suicide-self-harm': 'Track 1: Suicide and Self-Harm Support',
          'identity-depression-anxiety': 'Track 2: Identity, Depression and Anxiety Support',
          'trauma-behavioural': 'Track 3: Trauma and Behavioural Patterns'
        }
        const selectedTrackLabel = trackLabels[formData.value.supportFocus.track] || formData.value.supportFocus.track

        // Create HTML email content matching Safe Haven design
        const htmlContent = `
          <div style="font-family: Georgia, serif; max-width: 600px; margin: 0 auto; background-color: #f8f6f0; padding: 40px; border-radius: 12px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #2d4a2b; font-size: 32px; font-weight: bold; font-style: italic; margin: 0;">Safe Haven Wellness Group</h1>
              <div style="width: 80px; height: 3px; background-color: #c17d5e; margin: 15px auto;"></div>
              <h2 style="color: #5a7059; font-size: 18px; font-style: italic; margin: 10px 0;">New Membership Application</h2>
            </div>
            
            <div style="background-color: white; padding: 30px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin-bottom: 20px; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">1) About You</h3>
              <p style="color: #5a7059; margin: 10px 0;"><strong>Full Name:</strong> ${formData.value.name}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>Mobile Number:</strong> ${formData.value.mobileNumber}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>Age:</strong> ${formData.value.age}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>Occupation:</strong> ${formData.value.occupation}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>Email:</strong> ${formData.value.email}</p>
              
              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin: 30px 0 20px 0; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">2) Understanding Safe Haven</h3>
              <p style="color: #5a7059; margin: 10px 0;"><strong>✓ Not Clinical Service:</strong> ${formData.value.understanding.notClinical ? 'Acknowledged' : 'Not acknowledged'}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>✓ Not Emergency Service:</strong> ${formData.value.understanding.notEmergency ? 'Acknowledged' : 'Not acknowledged'}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>✓ Placement Determined by Team:</strong> ${formData.value.understanding.placementDetermined ? 'Acknowledged' : 'Not acknowledged'}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>✓ Agrees to Guidelines:</strong> ${formData.value.understanding.agreeGuidelines ? 'Acknowledged' : 'Not acknowledged'}</p>
              
              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin: 30px 0 20px 0; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">3) Experiences That Have Shaped You</h3>
              ${selectedExperiences.length > 0 ? 
                selectedExperiences.map(exp => `<p style="color: #5a7059; margin: 8px 0;">• ${exp}</p>`).join('') :
                '<p style="color: #5a7059; margin: 10px 0; font-style: italic;">No experiences selected</p>'
              }
              
              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin: 30px 0 20px 0; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">4) My Situation Lately</h3>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Keep myself safe:</strong> ${formData.value.situation.keepSafe}</p>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Flashbacks/nightmares/intrusive thoughts:</strong> ${formData.value.situation.flashbacks}</p>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Currently seeking psychiatric help/medication:</strong> ${formData.value.situation.psychiatricHelp}</p>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Has external support:</strong> ${formData.value.situation.externalSupport}</p>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Can pause/ground/seek support:</strong> ${formData.value.situation.emotionalRegulation}</p>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Can avoid highly triggering details:</strong> ${formData.value.situation.noTriggeringDetails}</p>

              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin: 30px 0 20px 0; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">5) Support Focus</h3>
              <p style="color: #5a7059; margin: 8px 0;"><strong>What's causing the most stress right now:</strong></p>
              <p style="color: #5a7059; margin: 8px 0; line-height: 1.6;">${formData.value.supportFocus.stressArea}</p>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Support Track Alignment:</strong> ${selectedTrackLabel}</p>

              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin: 30px 0 20px 0; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">6) Goals for Joining Safe Haven</h3>
              <p style="color: #5a7059; margin: 10px 0; line-height: 1.6;">${formData.value.goals}</p>

              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin: 30px 0 20px 0; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">7) Readiness for Group-Based Wellness Spaces</h3>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Respects boundaries:</strong> ${formData.value.readiness.respectBoundaries}</p>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Open to regulation skills:</strong> ${formData.value.readiness.openToSkills}</p>
              <p style="color: #5a7059; margin: 8px 0;"><strong>Can receive facilitator guidance:</strong> ${formData.value.readiness.receiveFeedback}</p>

              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin: 30px 0 20px 0; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">8) Support Needs Check</h3>
              ${selectedSupportNeeds.length > 0 ?
                selectedSupportNeeds.map(item => `<p style="color: #5a7059; margin: 8px 0;">• ${item}</p>`).join('') :
                '<p style="color: #5a7059; margin: 10px 0; font-style: italic;">No support needs selected</p>'
              }
              
              <h3 style="color: #2d4a2b; font-size: 20px; font-weight: bold; font-style: italic; margin: 30px 0 20px 0; border-bottom: 2px solid #c17d5e; padding-bottom: 10px;">9) Consent & Acknowledgement</h3>
              <p style="color: #5a7059; margin: 10px 0;"><strong>✓ Consent to Review:</strong> ${formData.value.consent.reviewed ? 'Given' : 'Not given'}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>✓ Voluntary Participation:</strong> ${formData.value.consent.voluntary ? 'Acknowledged' : 'Not acknowledged'}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>Name:</strong> ${formData.value.name}</p>
              <p style="color: #5a7059; margin: 10px 0;"><strong>Date:</strong> ${formData.value.date}</p>
            </div>
            
            <div style="text-align: center; margin-top: 30px; padding: 20px; background-color: #e8e2d5; border-radius: 8px;">
              <p style="color: #5a7059; font-style: italic; margin: 0;">Healing through the power of Lived Mental Health Experience (LMHE)</p>
              <p style="color: #5a7059; font-size: 14px; margin: 10px 0 0 0;">A space where survival becomes wisdom.</p>
            </div>
          </div>
        `

        // Create text version for accessibility
        const textContent = `
SAFE HAVEN WELLNESS GROUP - NEW MEMBERSHIP APPLICATION

1) ABOUT YOU
Full Name: ${formData.value.name}
Mobile Number: ${formData.value.mobileNumber}
Age: ${formData.value.age}
Occupation: ${formData.value.occupation}
Email: ${formData.value.email}

2) UNDERSTANDING SAFE HAVEN
✓ Not Clinical Service: ${formData.value.understanding.notClinical ? 'Acknowledged' : 'Not acknowledged'}
✓ Not Emergency Service: ${formData.value.understanding.notEmergency ? 'Acknowledged' : 'Not acknowledged'}
✓ Placement Determined by Team: ${formData.value.understanding.placementDetermined ? 'Acknowledged' : 'Not acknowledged'}
✓ Agrees to Guidelines: ${formData.value.understanding.agreeGuidelines ? 'Acknowledged' : 'Not acknowledged'}

3) EXPERIENCES THAT HAVE SHAPED YOU
${selectedExperiences.length > 0 ? selectedExperiences.map(exp => `• ${exp}`).join('\n') : 'No experiences selected'}

4) MY SITUATION LATELY
Keep myself safe: ${formData.value.situation.keepSafe}
Flashbacks/nightmares/intrusive thoughts: ${formData.value.situation.flashbacks}
Currently seeking psychiatric help/medication: ${formData.value.situation.psychiatricHelp}
Has external support: ${formData.value.situation.externalSupport}
Can pause/ground/seek support: ${formData.value.situation.emotionalRegulation}
Can avoid highly triggering details: ${formData.value.situation.noTriggeringDetails}

5) SUPPORT FOCUS
What's causing the most stress right now: ${formData.value.supportFocus.stressArea}
Support Track Alignment: ${selectedTrackLabel}

6) GOALS FOR JOINING SAFE HAVEN
${formData.value.goals}

7) READINESS FOR GROUP-BASED WELLNESS SPACES
Respects boundaries: ${formData.value.readiness.respectBoundaries}
Open to regulation skills: ${formData.value.readiness.openToSkills}
Can receive facilitator guidance: ${formData.value.readiness.receiveFeedback}

8) SUPPORT NEEDS CHECK
${selectedSupportNeeds.length > 0 ? selectedSupportNeeds.map(item => `• ${item}`).join('\n') : 'No support needs selected'}

9) CONSENT & ACKNOWLEDGEMENT
✓ Consent to Review: ${formData.value.consent.reviewed ? 'Given' : 'Not given'}
✓ Voluntary Participation: ${formData.value.consent.voluntary ? 'Acknowledged' : 'Not acknowledged'}
Name: ${formData.value.name}
Date: ${formData.value.date}

---
Healing through the power of Lived Mental Health Experience (LMHE)
A space where survival becomes wisdom.
        `

        const emailData = {
          to: ['d.sitati@yahoo.com','amisarashid37@gmail.com','safehavengroup.ke@gmail.com'],
          subject: `Safe Haven Application: ${formData.value.name}`,
          html: htmlContent,
          text: textContent
        }

        const response = await fetch('https://mmvwylsqsgnaetlldqjp.supabase.co/functions/v1/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1tdnd5bHNxc2duYWV0bGxkcWpwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM1NDY5MDEsImV4cCI6MjA3OTEyMjkwMX0.xO431vCaaUWTplKKG5PAI-M5lP30AiPWRdgAzFQeZb0'
          },
          body: JSON.stringify(emailData)
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to send notification')
        }

        submitStatus.value = 'success'
        submitMessage.value = 'Thank you for your application! We have received your information and will be in touch soon about next steps.'
        
      } catch (error) {
        submitStatus.value = 'error'
        submitMessage.value = 'There was an error submitting your application. Please try again or contact us directly.'
      } finally {
        isSubmitting.value = false
      }
    }

    const handleOpenEvent = () => {
      openModal()
    }

    watch(
      () => [route.name, route.hash],
      () => {
        if (isJoinRoute()) {
          openModal()
        } else if (showModal.value) {
          closeModal({ clearRoute: false })
        }
      }
    )

    onMounted(() => {
      window.addEventListener('open-join-modal', handleOpenEvent)
      if (isJoinRoute()) {
        openModal()
      }
    })

    onUnmounted(() => {
      window.removeEventListener('open-join-modal', handleOpenEvent)
      document.body.style.overflow = ''
    })

    return {
      showModal,
      currentStep,
      totalSteps,
      stepNumbers,
      formData,
      stressAreaWordCount,
      isSubmitting,
      submitStatus,
      submitMessage,
      isCurrentStepValid,
      openModal,
      closeModal,
      nextStep,
      previousStep,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.modal-enter-active {
  transition: opacity 0.3s ease-out;
}

.modal-leave-active {
  transition: opacity 0.3s ease-in;
}

.modal-enter-active .relative {
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

.modal-leave-active .relative {
  transition: transform 0.3s ease-in, opacity 0.3s ease-in;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.9);
  opacity: 0;
}

input[type="radio"] {
  width: 16px;
  height: 16px;
  accent-color: var(--terracotta);
  cursor: pointer;
}

label:has(input[type="radio"]) {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid var(--sand);
  border-radius: 9999px;
  padding: 4px 10px;
}

label.track-option:has(input[type="radio"]) {
  border-radius: 0.5rem;
  padding: 0.75rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
